/**
 * <LeadForm> — unified lead-capture component for the listing-conversion
 * overhaul (Unit 4 of the plan).
 *
 * Variant-driven: reads `leadForm` + `meta` from `useListingVariant()` or
 * from the explicit `variantOverride` prop (used by /tradicional and
 * /flotillas standalone pages — see Unit 9). Renders 1-3 steps based on
 * the variant's `steps`, hides itself if `enabled` is false.
 *
 * The actual mutation lives in the caller — `<LeadForm>` is rendered
 * with an `onSubmit` callback (Unit 8 will wire it). For Unit 4 we
 * build the SHELL and STATE MACHINE only.
 *
 * Pixel events:
 *   - Step 1 mount fires `addToCart({ subEvent })` derived from
 *     `variant.leadForm.subType`. Skipped when `subType === null` OR
 *     `leadType === 'supply'` (supply-side firing is the caller's job
 *     via `useSupplyLead`, per CLAUDE.md "Supply vs Demand event split").
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  useContactPersist,
  useContactPrefill
} from 'src/hooks/useContactPrefill'
import {
  buildLeadKey,
  clearLeadSubmission,
  getLastLeadSubmission,
  markLeadSubmitted
} from 'src/lib/leadFormStorage'
import { useListingVariant } from 'src/lib/listingVariants'
import type {
  LeadFormChannel,
  LeadFormConfig,
  LeadFormStep,
  ListingVariantMeta
} from 'src/lib/listingVariants/types'
import {
  StorageTime,
  type IConfiguration,
  type ILocation
} from 'src/types/graphql'
import { verifyBotId } from 'src/utils/botid'
import useAddToCart from 'src/utils/pixels/common'
import {
  getAnalyticsDealType,
  getAnalyticsLocationType
} from 'src/utils/pixels/enrichment'
import posthog from 'src/utils/pixels/posthog'
import { addToCart } from 'src/utils/pixels/spotmeEvents'

import RevisitScreen from 'src/components/LeadForm/RevisitScreen'
import SpecialistCard from 'src/components/LeadForm/SpecialistCard'
import Step1Qualifier from 'src/components/LeadForm/steps/Step1Qualifier'
import Step2Contact from 'src/components/LeadForm/steps/Step2Contact'
import Step3Success from 'src/components/LeadForm/steps/Step3Success'
import {
  useLeadFormState,
  type LeadFormValues
} from 'src/components/LeadForm/useLeadFormState'

/**
 * Max time the submit waits for the BotID pre-flight verdict before
 * proceeding unflagged. Deliberately far below verifyBotId's own 8s
 * timeout: fast verdicts (desktop / good networks) ride the mutation so
 * the server can skip host notifications for flagged sessions; slow
 * mobile round-trips fall back to the unflagged submit rather than
 * visibly delaying the user.
 */
const BOTID_SUBMIT_WAIT_MS = 2500

/**
 * What counts as a "real interaction" for the inline-mount funnel gate below
 * (`markInteracted`). Clicks are filtered to interactive descendants so a
 * stray click on the card's whitespace / copy does not arm the funnel.
 */
const INTERACTIVE_TARGET_SELECTOR =
  'button, input, select, textarea, label, a, [role="button"], [role="option"], [role="radio"], [role="checkbox"]'

export type LeadFormContainer =
  | 'inline-on-card'
  | 'mobile-sheet'
  | 'page-section'

export type LeadFormSource =
  | 'listing_broker_modal'
  | 'listing_traditional_modal'
  | 'listing_fleet_modal'
  | 'tradicional_page'
  | 'estacionamiento_page'
  | 'bodegas_page'
  | 'flotillas_full'
  | 'logistics_page'
  | 'developer_page'

export type LeadFormVariantOverride = {
  leadForm: LeadFormConfig
  meta: Pick<ListingVariantMeta, 'dealType' | 'locationType'>
}

export type LeadFormProps = {
  /**
   * When provided, overrides the variant pulled from `useListingVariant()`.
   * Used by standalone surfaces (`/tradicional`, `/pension-estacionamiento/
   * flotillas`) that don't sit inside a `ListingVariantProvider`.
   */
  variantOverride?: LeadFormVariantOverride
  container: LeadFormContainer
  leadSource: LeadFormSource
  /**
   * Force compact mode (placeholder-only fields, tighter gaps, no privacy
   * footnote) regardless of container. By default `compact` is `true` only
   * for `container === 'inline-on-card'`. Flotillas hero overrides this on
   * `'page-section'` so the right-rail form fits 13" MBP above the fold.
   */
  compact?: boolean
  /**
   * `'demand'` fires `addToCart` on Step 1 mount. `'supply'` skips that
   * — supply pixels fire in the caller via `useSupplyLead` per CLAUDE.md.
   */
  leadType?: 'demand' | 'supply'
  location?: ILocation | null
  configuration?: IConfiguration | null
  initialValues?: Partial<LeadFormValues>
  onClose?: () => void
  /**
   * Called when the user submits the form. The caller wires the actual
   * mutation (e.g. `sendCustomerInfoEmail`) and any WhatsApp redirect.
   * Returning a rejected promise puts the form into an error state.
   */
  onSubmit: (values: LeadFormValues, channel: LeadFormChannel) => Promise<void>
  /**
   * Per-instance override for the Step 2 primary CTA. Falls back to
   * `variant.leadForm.primaryChannel` when omitted.
   *
   * Used by the Broker / Traditional listing-page email-secondary CTAs
   * ("Solicitar por correo" desktop button + mobile `<LeadFormInlineCta>`):
   * the user explicitly picked email, so the drawer should open with the
   * blue email pill on top and the WhatsApp link as the secondary. Without
   * this override the drawer inherits the variant's `primaryChannel:
   * 'whatsapp'` and reads as if the email button did nothing.
   *
   * Every other LeadForm caller (Fleet pill, /flotillas hero, /tradicional
   * standalone hero, Specialist card) leaves this undefined so the variant's
   * `primaryChannel` decides — WhatsApp stays primary there.
   */
  primaryChannelOverride?: LeadFormChannel
  /**
   * Controls how the SpecialistCard renders alongside the steps.
   *   - `'auto'` (default): renders on every step except success, using
   *     the inline (64px) layout on `page-section` containers and the
   *     compact (48px) layout elsewhere. This is what listing-context
   *     surfaces and the standalone /tradicional page want.
   *   - `'step1-only-compact'`: renders only on the first step and
   *     forces the compact (48px) layout regardless of container.
   *   - `'hidden'`: suppresses the card when the surrounding surface
   *     already offers direct contact (the Flotillas hero).
   */
  specialistDisplay?: 'auto' | 'step1-only-compact' | 'hidden'
}

/**
 * Container-driven outer rhythm. The form's children (step components,
 * RevisitScreen, Step3Success) each have their own internal spacing —
 * this just sets the vertical breathing between them.
 *
 *   - `page-section` (Flotillas hero, /tradicional standalone page) gets
 *     the most generous canvas since the form IS the hero.
 *   - `inline-on-card` (desktop price-card always-expanded form) is
 *     height-constrained; tightest rhythm so the renter sees the form
 *     AND the listing detail on the same screen.
 *   - `mobile-sheet` (modal drawer) sits in the middle — there's more
 *     vertical room than the price card, but no need for hero-level air.
 */
function containerClassName(
  container: LeadFormContainer,
  compact: boolean
): string {
  if (compact) return 'flex flex-col gap-3'
  if (container === 'page-section') return 'flex flex-col gap-6'
  if (container === 'inline-on-card') return 'flex flex-col gap-3'
  return 'flex flex-col gap-5'
}

export default function LeadForm({
  variantOverride,
  container,
  leadSource,
  leadType = 'demand',
  location,
  configuration,
  initialValues,
  onClose,
  onSubmit,
  compact: compactOverride,
  specialistDisplay = 'auto',
  primaryChannelOverride
}: LeadFormProps) {
  const handleAddToCart = useAddToCart()

  // Pull the variant from context unless the caller forces an override.
  // useListingVariant() throws outside a provider, so when an override
  // is present we MUST short-circuit before calling it.
  const variantFromContext = variantOverride
    ? null
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useListingVariant()

  const leadForm: LeadFormConfig =
    variantOverride?.leadForm ?? variantFromContext!.leadForm
  const meta: Pick<ListingVariantMeta, 'dealType' | 'locationType'> =
    variantOverride?.meta ??
    ({
      dealType: variantFromContext!.meta.dealType,
      locationType: variantFromContext!.meta.locationType
    } as const)

  // FIX 1 — Skip the qualifier step on listing-context surfaces.
  // When the user already opened a specific listing, asking them to
  // re-type the sqm is friction. We skip the qualifier and seed `sqm`
  // from `configuration.size` so the WA prefill still carries it.
  //
  // Carve-outs:
  //   - Only skip when `qualifierField === 'sqm'`. Fleet's quantity
  //     qualifier (`qualifierField === 'quantity'`) STAYS — the user is
  //     specifying THEIR fleet size, not the listing's availability.
  //   - On the standalone /pension-estacionamiento/flotillas page the
  //     `configuration` prop is null, so this short-circuits naturally.
  const hasListingContext = configuration != null
  const shouldSkipQualifier =
    hasListingContext &&
    leadForm.qualifierField === 'sqm' &&
    leadForm.steps.includes('qualifier')

  const effectiveSteps = useMemo<ReadonlyArray<LeadFormStep>>(() => {
    if (!shouldSkipQualifier) return leadForm.steps
    return leadForm.steps.filter((step) => step !== 'qualifier')
  }, [shouldSkipQualifier, leadForm.steps])

  // Contact prefill — promoted to the shared `useContactPrefill` hook
  // so every contact-collecting surface across the app pulls from the
  // same store (`spotme_lead_prefill_v1`, 30-day TTL) and the same
  // authed/anon merge logic. Precedence is lowest→highest:
  //
  //   1. anonymous localStorage  (hook returns it after hydration if
  //                              no currentUser is present)
  //   2. authed user profile     (hook returns it from currentUser;
  //                              SSR-safe because auth state seeds
  //                              the same way on server and client)
  //   3. caller `initialValues`  (listing context / URL params — wins)
  //   4. sqm seeded from config  (applies on top of all of the above)
  //
  // The hook is hydration-safe: `prefill` is empty during SSR + the
  // hydration render, then quietly resolves on the post-hydration
  // commit. We layer it into `seededInitialValues` for the authed/
  // caller paths and inject anonymous values via `setField` after
  // hydration (since `useLeadFormState` locks initial values on first
  // render — see the injection effect further down).
  // Note: we intentionally don't subscribe to `source` / `clearAnonPrefill`
  // here. The LeadForm is opened deliberately (modal click, hero CTA) so
  // a "¿No eres tú?" affordance carries less weight than on standalone
  // landing forms where the renter lands on a prefilled form. Standalone
  // forms (Calculate, B2B, OnDemand, Apply, etc.) DO render
  // `<ClearPrefillLink>` from this hook's `source` / `clearAnonPrefill`.
  const { prefill, hasHydrated, isAuthed } = useContactPrefill()
  const persistContact = useContactPersist()

  // Layered initial values for `useLeadFormState`. The hook's `prefill`
  // is empty during SSR + hydration render (for SSR safety), then
  // authed values arrive synchronously while anon values arrive on the
  // post-hydration commit. Since `useLeadFormState` locks initial
  // values on first render, the authed/caller paths land here and the
  // anon path is filled in via the injection effect below.
  const seededInitialValues = useMemo<
    Partial<LeadFormValues> | undefined
  >(() => {
    const layered: Partial<LeadFormValues> = {
      ...prefill,
      ...(initialValues ?? {})
    }
    const hasLayered = Object.keys(layered).length > 0

    if (!shouldSkipQualifier) {
      return hasLayered ? layered : initialValues
    }

    const width = configuration?.size?.width
    const length = configuration?.size?.length
    if (typeof width !== 'number' || typeof length !== 'number') {
      return hasLayered ? layered : initialValues
    }
    const computedSqm = Math.round(width * length)
    if (!Number.isFinite(computedSqm) || computedSqm <= 0) {
      return hasLayered ? layered : initialValues
    }
    return { ...layered, sqm: computedSqm }
  }, [shouldSkipQualifier, configuration, initialValues, prefill])

  // Per-listing dedupe — when the same browser has already submitted
  // a lead for the same `(leadSource × configurationId)` pair within
  // the 7-day TTL, render `<RevisitScreen>` instead of the step
  // machine so the user doesn't accidentally double-submit. Flipping
  // `revisit` back to false via `handleSendAnother` clears the entry
  // and re-mounts the form normally.
  //
  // Demand-only. Supply mounts and disabled forms never enter revisit
  // mode (different funnel, different dedupe semantics).
  const leadKey = useMemo(
    () =>
      buildLeadKey({
        leadSource,
        configurationId: configuration?.id ?? null
      }),
    [leadSource, configuration?.id]
  )

  // Revisit lookup is also localStorage-bound and so must be gated on
  // `hasHydrated` for the same SSR-safety reason as `anonPrefill` above.
  // Initial state is `false` (no revisit screen during SSR or first
  // client render). After hydration, the effect below checks localStorage
  // and flips to true if a dedupe entry is present for this `leadKey`.
  // Result: users with a recent submission briefly see the LeadForm
  // before it swaps to <RevisitScreen> on the post-hydration commit —
  // acceptable, since the alternative is the React #418 hydration error
  // we used to throw when we tried to read localStorage during the
  // first render.
  const [revisit, setRevisit] = useState<boolean>(false)
  useEffect(() => {
    if (!hasHydrated) return
    if (leadType !== 'demand') return
    if (getLastLeadSubmission(leadKey) != null) {
      setRevisit(true)
    }
  }, [hasHydrated, leadType, leadKey])

  const handleSendAnother = useCallback(() => {
    clearLeadSubmission(leadKey)
    setRevisit(false)
  }, [leadKey])

  // BotID pre-flight. SpotMe lead forms submit via Apollo cross-origin
  // to spotme-server, which bypasses Next.js — so BotID can't attach to
  // those requests directly. We make a tiny POST to /api/botid/verify
  // (the only path declared in instrumentation-client.ts) before every
  // caller's onSubmit, log the result to PostHog (`botid_check` event),
  // and ALWAYS proceed with the submission regardless of verdict.
  //
  // Soft-flag policy is deliberate: B2B lead value is asymmetric — a
  // missed real lead costs more than a junk lead in HubSpot. Sales
  // triages flagged contacts there. See docs/marketing/botid.md.
  //
  // The verdict IS forwarded when it arrives in time: we wait up to
  // BOTID_SUBMIT_WAIT_MS for it and thread `botIdFlagged` into the values
  // so the server can skip the HOST/PARTNER notification for bot-flagged
  // sessions (the lead itself always proceeds). On timeout we submit
  // unflagged — fail-open, identical to the old fire-and-forget — and the
  // helper still logs the late verdict to PostHog in the background.
  const botidSurface = `leadform_${leadType}_${meta.dealType.toLowerCase()}`
  const wrappedOnSubmit = useCallback(
    async (values: LeadFormValues, channel: LeadFormChannel) => {
      // The helper never throws — failure modes resolve as
      // { isBot: false, checkFailed: true }.
      const verdict = await Promise.race([
        verifyBotId({ surface: botidSurface }),
        new Promise<undefined>((resolve) =>
          setTimeout(() => resolve(undefined), BOTID_SUBMIT_WAIT_MS)
        )
      ])
      return onSubmit(
        verdict?.isBot === true ? { ...values, botIdFlagged: true } : values,
        channel
      )
    },
    [onSubmit, botidSurface]
  )

  const state = useLeadFormState({
    steps: effectiveSteps,
    initialValues: seededInitialValues,
    onSubmit: wrappedOnSubmit
  })

  // Anonymous prefill INJECTION after hydration. `useLeadFormState` locks
  // its initial values into a ref on first render — and since the
  // hook's anon prefill is null during SSR + hydration render (for SSR
  // safety), it never lands via the `initialValues` path. We backfill
  // via `setField` here once after hydration. Authed users skip this
  // path (their data flows through `seededInitialValues` synchronously
  // via `prefill`). Only fires when the field is still empty so any
  // caller-provided initialValues / authed prefill already in place
  // takes precedence.
  const anonPrefillInjectedRef = useRef(false)
  useEffect(() => {
    if (!hasHydrated) return
    if (isAuthed) return
    if (anonPrefillInjectedRef.current) return
    if (!prefill.name && !prefill.phone && !prefill.email) return
    anonPrefillInjectedRef.current = true
    if (prefill.name && !state.values.name) state.setField('name', prefill.name)
    if (prefill.phone && !state.values.phone) {
      state.setField('phone', prefill.phone)
    }
    if (prefill.email && !state.values.email) {
      state.setField('email', prefill.email)
    }
    // We deliberately omit state.values / state.setField / prefill from
    // deps — setField is stable (useCallback), state.values is captured
    // on the first post-hydration render where the fields are still
    // empty (the user hasn't had a chance to type yet since JS was just
    // bootstrapped). Including state.values would re-fire and clobber
    // user input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated, isAuthed])

  // Engagement pixel — demand-only. The supply-side mirror is fired by
  // the caller via `useSupplyLead` (CLAUDE.md Marketing Attribution).
  //
  // FIRES ON FIRST USER INTERACTION (field focus or any submit), NOT on
  // mount. Rationale: the desktop PriceCard mounts this LeadForm inline
  // for every Broker/Traditional listing pageview (no click gate — form
  // is always visible in the price rail per Mexican-marketplace
  // conventions). A mount-time fire equates `broker_contact` /
  // `traditional_contact` to a pageview, NOT to engagement, and inflates
  // the metric 3-20x versus `view_item` (verified in PostHog
  // 2026-05-19: ca50c5f9 Monterrey/Buenos Aires listing had 116
  // view_items vs 128 traditional_contacts). Real intent is the user
  // touching the form. PMax-retargeted sessions re-pageview the same
  // listing 3-4x; the sessionStorage gate below collapses those back to
  // a single event per (session × leadSource × configurationId × subType).
  //
  // The supply-side mirror (useSupplyLead) is unaffected — it's a
  // separate code path with its own intent rules.
  const hasFiredAddToCartRef = useRef(false)
  const fireAddToCartIfNeeded = useCallback(() => {
    if (hasFiredAddToCartRef.current) return
    if (!leadForm.enabled) return
    if (leadType !== 'demand') return
    if (leadForm.subType === null) return
    // Defensive guard — `broker_contact` and `traditional_contact` are
    // semantically LISTING-LEVEL intents (per docs/marketing/analytics-
    // reference.md §1.3). They MUST NOT fire from a landing surface that
    // auto-mounts the form (Flotillas hero, /tradicional). Those surfaces
    // already set `subType: null` to opt out of the fire — this is the
    // belt-and-suspenders layer so a future landing surface that forgets
    // the override doesn't silently re-inflate the metric.
    if (
      (leadForm.subType === 'broker_contact' ||
        leadForm.subType === 'traditional_contact') &&
      configuration == null
    ) {
      return
    }

    // Session-level dedupe — covers SPA navigation back-and-forth to the
    // same listing AND multiple LeadForm remounts (e.g. variant flips,
    // size-picker re-renders) within the same browser session.
    const dedupeKey = `atc_fired:${leadSource}:${configuration?.id ?? 'none'}:${leadForm.subType}`
    try {
      if (
        typeof window !== 'undefined' &&
        window.sessionStorage.getItem(dedupeKey)
      ) {
        hasFiredAddToCartRef.current = true
        return
      }
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(dedupeKey, '1')
      }
    } catch {
      // sessionStorage unavailable (Safari private mode, quota) — proceed
      // without cross-mount dedupe; the in-mount ref still gates re-fires.
    }
    hasFiredAddToCartRef.current = true

    const spaceType = meta.locationType ?? ''
    // Pull storageTime + quantity from state.values so PostHog/GA4/Meta
    // see `duration_bucket` + `requested_months` on the canonical
    // add_to_cart fire. PriceCard / Header seed these when known
    // (storage listings with a picker, fleet flows). Long-stay deals (6+
    // months) are the primary objective tier and need this enrichment.
    addToCart({
      subEvent: leadForm.subType,
      location: location ?? undefined,
      config: configuration ?? null,
      spaceType,
      storageTime: (state.values.when ?? '') as StorageTime | '',
      quantity: state.values.quantity ?? undefined
    })
  }, [
    leadForm.enabled,
    leadForm.subType,
    leadType,
    leadSource,
    configuration,
    location,
    meta.locationType,
    state.values.when,
    state.values.quantity
  ])

  // Anonymous-prefill persistence + per-listing dedupe write — when the
  // form reaches `success` we do two things:
  //
  //   1. Mirror name / phone / email into the prefill storage so the
  //      next surface this browser opens lands pre-filled. Anonymous
  //      users only — authed users have `currentUser` as the
  //      source of truth.
  //   2. Mark the `leadKey` as submitted in the dedupe map so a
  //      re-visit within 7 days lands on `<RevisitScreen>` instead of
  //      a fresh form. This applies regardless of auth state — dedupe
  //      is UX-only.
  //
  // The effect deliberately depends only on `state.step` so we don't
  // re-persist on every keystroke as `state.values` change.
  useEffect(() => {
    if (state.step !== 'success') return
    if (leadType !== 'demand') return

    // Dedupe write — both authed + anonymous get marked so revisits
    // see the RevisitScreen.
    markLeadSubmitted(leadKey)

    // Prefill write — anonymous only. Authed users' canonical data
    // lives in currentUser; mirroring their info into the shared anon
    // store would leak across a shared-device hand-off.
    if (isAuthed) return
    persistContact({
      name: state.values.name,
      phone: state.values.phone,
      email: state.values.email
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step])

  // Inline-mount funnel gate (2026-09-14). `page-section` heroes (Flotillas,
  // /soluciones-logisticas, /tradicional, /bodegas, /estacionamiento) and the
  // `inline-on-card` desktop price rail render this form on page load, so a
  // mount-time `lead_form_step{contact}` equalled a pageview: on
  // /pension-estacionamiento/flotillas (paid Demand Gen lander, ~7k sessions/
  // month) it fired in 98% of sessions and `lead_form_abandoned` in 24% —
  // both meaningless there, and the abandon fire poisoned the "started but
  // did not finish" retargeting audience. For those mounts BOTH events are
  // gated behind the first real interaction: field focus / change, or a
  // click on an interactive descendant (pills are <button>s and macOS Safari
  // does not focus buttons on click — same reason `logistics_lead_start`
  // wires its pill handlers). The modal (`mobile-sheet`) opens on an
  // explicit CTA click, so opening IS the interaction: it starts armed and
  // keeps firing on open, exactly as before.
  const isInlineMount = container !== 'mobile-sheet'
  const [hasInteracted, setHasInteracted] = useState(!isInlineMount)
  const hasInteractedRef = useRef(!isInlineMount)
  const markInteracted = useCallback(() => {
    if (hasInteractedRef.current) return
    hasInteractedRef.current = true
    setHasInteracted(true)
  }, [])

  // Per-step funnel anchor — fires `lead_form_step` on first arrival at each
  // step (qualifier → contact → success) so PostHog can build a per-modal
  // funnel and surface Step1→Step2 drop-off. Carries `sub_event` + `lead_source`
  // so funnels can be sliced by Broker / Traditional / Fleet / B2B-pillar.
  // Skipped for supply-side mounts (those go through useSupplyLead's
  // supply_lead_start / supply_lead events). On inline mounts the FIRST
  // fire waits for `hasInteracted` (see the gate above), so "arrival" at
  // the initial step means "the visitor touched the form", not "the page
  // loaded"; later steps fire on transition as before.
  const leadFormStepFiredRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!leadForm.enabled) return
    if (leadType !== 'demand') return
    if (!hasInteracted) return
    const stepName = state.step
    if (leadFormStepFiredRef.current.has(stepName)) return
    leadFormStepFiredRef.current.add(stepName)
    posthog.event('lead_form_step', {
      step: stepName,
      step_index: state.stepIndex,
      sub_event: leadForm.subType ?? undefined,
      lead_source: leadSource,
      // Always pipe through the analytics helpers — meta.dealType /
      // meta.locationType are raw UPPERCASE GraphQL enums; PostHog
      // funnels and ad-platform conversion goals slice on lowercase.
      deal_type: getAnalyticsDealType(meta.dealType),
      location_type: getAnalyticsLocationType(meta.locationType)
    })
  }, [
    leadForm.enabled,
    leadForm.subType,
    leadType,
    hasInteracted,
    state.step,
    state.stepIndex,
    leadSource,
    meta.dealType,
    meta.locationType
  ])

  // Abandonment funnel — fires `lead_form_abandoned` if the user closes
  // the modal (or navigates away) WITHOUT reaching the success step.
  // Funnel drop-off via `lead_form_step` already shows this implicitly,
  // but an explicit event lets Meta / Google retarget "started but did
  // not finish" as an audience and gives the abandonment a concrete
  // timestamp. Demand-only — supply abandons aren't worth a separate
  // event today.
  //
  // Fires from TWO paths, guarded by a once-per-mount flag:
  //   1. Component unmount — the reliable path for modal-based mounts
  //      (Broker, Fleet, Traditional listing modals) where closing the
  //      modal triggers a clean React unmount.
  //   2. `visibilitychange` → `document.hidden` — the inline-hero mounts
  //      (Flotillas hero, /tradicional standalone) don't reliably unmount
  //      when the user closes the tab or backgrounds the browser. Without
  //      this listener we were undercounting abandonment on those
  //      surfaces. Trade-off: a tab-switch then return counts as
  //      abandonment, but the user's eventual success event is a clean
  //      signal in PostHog so cohort analysis can still net them out.
  //
  // Armed only once the visitor has interacted (`hasInteractedRef`) — an
  // inline mount nobody touched is a pageview, not an abandoned form.
  const reachedSuccessRef = useRef(false)
  const lastStepRef = useRef<string>(state.step)
  const abandonContextRef = useRef({
    enabled: leadForm.enabled,
    leadType,
    subType: leadForm.subType,
    leadSource,
    dealType: meta.dealType,
    locationType: meta.locationType
  })
  const abandonFiredRef = useRef(false)
  useEffect(() => {
    lastStepRef.current = state.step
    if (state.step === 'success') reachedSuccessRef.current = true
  }, [state.step])
  useEffect(() => {
    abandonContextRef.current = {
      enabled: leadForm.enabled,
      leadType,
      subType: leadForm.subType,
      leadSource,
      dealType: meta.dealType,
      locationType: meta.locationType
    }
  }, [
    leadForm.enabled,
    leadType,
    leadForm.subType,
    leadSource,
    meta.dealType,
    meta.locationType
  ])
  const fireAbandonedIfNeeded = useCallback(() => {
    if (abandonFiredRef.current) return
    if (!hasInteractedRef.current) return
    const ctx = abandonContextRef.current
    if (!ctx.enabled || ctx.leadType !== 'demand') return
    if (reachedSuccessRef.current) return
    abandonFiredRef.current = true
    posthog.event('lead_form_abandoned', {
      last_step: lastStepRef.current,
      sub_event: ctx.subType ?? undefined,
      lead_source: ctx.leadSource,
      deal_type: getAnalyticsDealType(ctx.dealType),
      location_type: getAnalyticsLocationType(ctx.locationType)
    })
  }, [])
  // Path 1 — unmount.
  useEffect(() => {
    return () => {
      fireAbandonedIfNeeded()
    }
  }, [fireAbandonedIfNeeded])
  // Path 2 — visibilitychange. Captures inline-hero mount abandons that
  // the unmount path misses (closing the tab, backgrounding the browser).
  useEffect(() => {
    if (typeof document === 'undefined') return
    const onVisibility = () => {
      if (document.hidden) fireAbandonedIfNeeded()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [fireAbandonedIfNeeded])

  if (!leadForm.enabled) {
    return null
  }

  // Per-listing dedupe short-circuit — render the "already submitted"
  // view in place of the step machine when this browser submitted to
  // the same `(leadSource × configurationId)` within the TTL window.
  // Demand-only by construction (the lazy-init for `revisit` checks
  // `leadType === 'demand'`).
  if (revisit) {
    return (
      <RevisitScreen
        container={container}
        onClose={onClose}
        onSendAnother={handleSendAnother}
      />
    )
  }

  // Render-time channel handler — the contact step calls this with the
  // chosen channel + an `overrides` object that carries fresh form values
  // past React's setState batching window (see useLeadFormState.submit
  // for rationale).
  //
  // Step-inversion routing (2026-05-18): contact is now Step 1. Submit
  // pre-creates the deal so partial completers land in sales' inbox,
  // THEN the form either jumps to success (WhatsApp path — the user is
  // leaving for WA, qualifier would be a dead room) or advances one step
  // into the qualifier (email path — the renter stays in the form for
  // optional enrichment). When the variant has no qualifier step (Broker
  // with listing context skips it via `effectiveSteps`), we go straight
  // to success regardless of channel.
  const handleStep2Submit = (
    channel: LeadFormChannel,
    overrides?: Partial<LeadFormValues>
  ) => {
    const hasEnrichmentStep = effectiveSteps.includes('qualifier')
    const destination: 'success' | 'next' =
      channel === 'whatsapp' || !hasEnrichmentStep ? 'success' : 'next'
    void state.submit({ channel, overrides, destination })
  }

  // Enrichment step continue — the qualifier step has been re-purposed
  // as OPTIONAL enrichment (Step 2 of the inverted flow). The deal was
  // already created at the contact step, so we never block here on
  // validation: any click on the qualifier's primary button advances
  // to success and fires `lead_form_enrich_submitted` with whatever
  // value the user provided. The `skipped` flag tells analytics whether
  // the renter actually answered or click-through'd past it.
  //
  // Plain function (not useCallback) because this lives after the
  // conditional returns above — `useCallback` would violate the rules
  // of hooks. Stability isn't needed; Step1Qualifier doesn't memo.
  const handleEnrichmentContinue = () => {
    const qualifierField = leadForm.qualifierField
    let qualifierValue: string | number | null = null
    if (qualifierField === 'sqm') {
      qualifierValue = state.values.sqm
    } else if (qualifierField === 'quantity') {
      qualifierValue = state.values.quantity
    } else if (qualifierField === 'when') {
      qualifierValue = state.values.when
    }
    const skipped =
      qualifierValue === null ||
      qualifierValue === '' ||
      (typeof qualifierValue === 'number' && qualifierValue <= 0)
    posthog.event('lead_form_enrich_submitted', {
      sub_event: leadForm.subType ?? undefined,
      lead_source: leadSource,
      deal_type: getAnalyticsDealType(meta.dealType),
      location_type: getAnalyticsLocationType(meta.locationType),
      qualifier_field: qualifierField ?? undefined,
      qualifier_value: skipped ? undefined : qualifierValue,
      skipped
    })
    state.next()
  }

  // Interaction handlers for the inline-mount funnel gate. Plain functions
  // (not useCallback) because they live after the conditional returns above.
  const handleFocusCapture = () => {
    markInteracted()
    fireAddToCartIfNeeded()
  }
  const handleClickCapture = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null
    if (target?.closest?.(INTERACTIVE_TARGET_SELECTOR)) markInteracted()
  }

  const isCompact = compactOverride ?? container === 'inline-on-card'

  return (
    <section
      className={containerClassName(container, isCompact)}
      data-lead-source={leadSource}
      data-lead-type={leadType}
      // First user interaction — fires the engagement add_to_cart pixel
      // (broker_contact / traditional_contact / contact_wa). Capture phase
      // catches focus events from any descendant input/textarea/select
      // without needing per-field wiring. Idempotent — re-fires are
      // gated by `hasFiredAddToCartRef` and a sessionStorage key. The same
      // focus (plus change / interactive click, for macOS Safari buttons)
      // arms the inline-mount `lead_form_step` / `lead_form_abandoned` gate.
      onFocusCapture={handleFocusCapture}
      onChangeCapture={markInteracted}
      onClickCapture={handleClickCapture}
    >
      {/* Progress bar removed 2026-05-18. With the inverted step order
          (contact → optional qualifier → success), most users land on
          success directly from the contact step via the WhatsApp pill,
          which means a 33% bar misleads more than it informs. The
          qualifier is intentionally OPTIONAL enrichment — the bar made
          it look mandatory. The form is also short enough that a
          progress indicator never carried much signal. */}

      {/*
        Demand-only. The SpecialistCard (Fernando) is a renter-facing
        trust signal — "Te conectamos en <30 min · coordino tu reserva".
        On supply surfaces (`/tradicional` host signup, future host
        landing pages) the audience is a property owner applying to
        list their inventory, NOT a renter looking for a unit. Showing
        Fernando there reads as a copy mismatch and doesn't serve the
        host's question ("will SpotMe bring me renters?"). If a
        partnership-manager persona is wanted on supply pages later,
        wire it as a separate variant or component — don't reuse
        Fernando.

        ALSO suppressed when `container === 'inline-on-card'` — the
        desktop price-card always-expanded inline form is height-
        constrained (renter wants to see the form AND the listing detail
        on the same screen). Fernando's compact card is ~70px which we
        can't afford in that rail. The "Asesor de SpotMe" trust pill
        rendered by PriceCard under the form carries the same signal in
        a single inline line. Fernando still appears on the mobile
        sheet and the /tradicional page-section. The Flotillas hero uses
        `specialistDisplay="hidden"` because it has its own WhatsApp action.
      */}
      {specialistDisplay !== 'hidden' &&
        leadForm.specialistKey != null &&
        state.step !== 'success' &&
        leadType === 'demand' &&
        container !== 'inline-on-card' &&
        !(
          specialistDisplay === 'step1-only-compact' && state.stepIndex !== 0
        ) && (
          <SpecialistCard
            specialistKey={leadForm.specialistKey}
            showResponseBadge={leadForm.responseBadge.show}
            layout={
              specialistDisplay === 'step1-only-compact'
                ? 'compact'
                : container === 'page-section'
                  ? 'inline'
                  : 'compact'
            }
            location={location ?? null}
            configuration={configuration ?? null}
            contextValues={state.values}
            // Traditional B2C self-storage gets the softer "concierge"
            // framing — see SpecialistCard for the rationale. Broker +
            // Fleet keep the specialist tone.
            tone={meta.dealType === 'TRADITIONAL' ? 'concierge' : 'specialist'}
            // Pixel + ES event for the WA shortcut inside the form. The
            // LeadForm Step 1 useEffect already fires the variant's
            // `broker_contact` / `traditional_contact` ADD_TO_CART on
            // drawer open — this is a SEPARATE intent (`contact_wa`)
            // since the renter is bypassing the form to WhatsApp directly.
            onWhatsappClick={
              location
                ? () => {
                    handleAddToCart(
                      'contact_wa',
                      location,
                      configuration,
                      state.values.quantity ?? undefined,
                      (state.values.when ?? '') as never
                    )
                  }
                : undefined
            }
          />
        )}

      {state.step === 'qualifier' && (
        <Step1Qualifier
          qualifierField={leadForm.qualifierField}
          values={state.values}
          setField={state.setField}
          onContinue={handleEnrichmentContinue}
        />
      )}

      {state.step === 'contact' && (
        <Step2Contact
          values={state.values}
          setField={state.setField}
          fields={leadForm.fields}
          primaryChannel={primaryChannelOverride ?? leadForm.primaryChannel}
          hideWhatsappFallback={leadForm.hideWhatsappFallback ?? false}
          useBookingFraming={leadForm.useBookingFraming ?? false}
          isSubmitting={state.isSubmitting}
          error={state.error}
          onSubmit={handleStep2Submit}
          onBack={state.stepIndex > 0 ? state.prev : undefined}
          // When nothing sits above Step 2 to anchor the page (no
          // SpecialistCard, demand-side only), let the step render its own
          // editorial header so it doesn't read as a bare form. Today this
          // fires for Traditional listings — `specialistKey: null` there
          // means the LeadForm jumps straight from progress bar to three
          // input fields. Broker + Fleet keep Fernando above and stay
          // header-less to avoid a stacked-frame look.
          //
          // Suppressed when `container === 'inline-on-card'` (desktop
          // always-expanded inline form inside the price card): the
          // price-card chrome already frames the form, so an additional
          // "Casi listo / Solo necesitamos cómo contactarte" header above
          // three input fields reads as a stutter. Mobile sheet + the
          // /tradicional page-section still get the header because they
          // mount without the surrounding price-card frame.
          showStepHeader={
            leadForm.specialistKey == null &&
            leadType === 'demand' &&
            container !== 'inline-on-card'
          }
          // The header's SUBTITLE is variant-selected, because the default
          // one promises a 2-hour SpotMe reply and this header renders on
          // exactly the surface where that is false (Traditional, mobile
          // sheet). See STEP2_HEADER_SUBTITLE in `steps/Step2Contact.tsx`.
          leadCopyKey={leadForm.leadCopyKey}
          // Tighten the vertical rhythm when the form is mounted inline
          // inside the price card, where the right rail is height-bound.
          // Flotillas hero opts in via the `compact` prop override (its
          // right rail is also constrained on 13" MBP).
          compact={isCompact}
        />
      )}

      {state.step === 'success' && (
        <Step3Success
          leadForm={leadForm}
          meta={meta}
          location={location}
          configuration={configuration}
          onClose={onClose}
          audience={leadType === 'supply' ? 'host' : 'renter'}
          contact={{ email: state.values.email, phone: state.values.phone }}
        />
      )}
    </section>
  )
}
